import { useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { startOfMinute } from 'date-fns';
import { styled } from '@mui/material/styles';
import Button from '@components/Button';
import { useTranslation } from '@hooks';
import { ResetIcon } from 'assets/images/icons';
import PulseAIInput from './PulseAIInput';
import Message from './Message';
import TypingIndicator from './TypeIndicator';

const Container = styled('div')({
    height: 'calc(var(--100vh) - 154px)',
    display: 'grid',
    gridGap: '16px',
    gridTemplateRows: '32px auto max-content',
    gridTemplateColumns: '100%',
    justifyContent: 'center',
    backgroundColor: '#f2f2f2',
    padding: '24px 16px',
    '@media (max-width: 768px)': {
        height: 'calc(var(--100vh) - 122px)',
        gridGap: '8px',
    },
});

export const Feed = styled('div')({
    overflowY: 'auto',
    display: 'flex',
    flexFlow: 'column-reverse',
    gap: 8,
    '@media (max-width: 768px)': {
        padding: 8,
    },
});

const PulseAIPlayground = ({ selectedAgentRef }) => {
    const [data, setData] = useState([]);
    const [isTyping, setIsTyping] = useState(false);

    const currentUser = useSelector(state => state.auth.user);

    const { t } = useTranslation();
    const conversationIdRef = useRef(uuidv4());

    const messages = useMemo(() => {
        if (!data.length) return [];
        const sortedMessages = [...data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        const getNormalizedDate = dateStr => startOfMinute(new Date(dateStr)).getTime();

        const createMessageObject = msg => ({ id: msg.id, message: msg.message, createdAt: msg.createdAt });

        return sortedMessages.reduce((groups, message) => {
            const lastGroup = groups[groups.length - 1];
            const messageTime = getNormalizedDate(message.createdAt);

            const shouldCreateNewGroup =
                !lastGroup ||
                messageTime !== getNormalizedDate(lastGroup.createdAt) ||
                message.type !== lastGroup.type ||
                message.modelType !== lastGroup.modelType;

            if (shouldCreateNewGroup) {
                groups.push({
                    ...message,
                    children: [createMessageObject(message)],
                    createdAt: new Date(messageTime),
                });
            } else {
                lastGroup.children.push(createMessageObject(message));
                lastGroup.children.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
            }

            return groups;
        }, []);
    }, [data]);

    const onReset = () => {
        setData([]);
        conversationIdRef.current = uuidv4();
    };

    return (
        <Container>
            <Button
                variant="outlined"
                sx={{ backgroundColor: '#fff', margin: '0 auto' }}
                startIcon={<ResetIcon />}
                onClick={onReset}
            >
                {t('buttons.reset')}
            </Button>
            <Feed>
                {isTyping && <TypingIndicator />}
                {messages.map((item, index) => (
                    <Message key={index} data={item} currentUser={currentUser} />
                ))}
            </Feed>
            <PulseAIInput
                selectedAgentRef={selectedAgentRef}
                setData={setData}
                conversationIdRef={conversationIdRef}
                isTyping={isTyping}
                setIsTyping={setIsTyping}
            />
        </Container>
    );
};

export default PulseAIPlayground;
